import { useI18n } from '@ecom/i18n';
import { Field, Modal, Select, TemporaryPasswordPanel } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useResetAdminPasswordMutation } from '../api';
import { METHOD_LABEL_KEY, VERIFICATION_METHODS, type VerificationMethod } from '../types';

import { PasswordActionDialog } from './PasswordActionDialog';

interface Issued {
  readonly account: string;
  readonly password: string;
  readonly expiresAt: string;
}

/**
 * A superadmin gives a platform ADMINISTRATOR a new provisional password. The one field added to the usual "reason + own password" is
 * the VERIFICATION METHOD: with no e-mail on the platform, the person asking was identified OUTSIDE it (a call, a video call, in person)
 * and that must leave a trace. The password is shown once by `TemporaryPasswordPanel` (masked, 60 seconds) and dropped from this
 * component's state the moment it is noted or the window ends. A superadmin is never offered this dialog (and the API refuses it).
 */
export function ResetAdminPasswordDialog({
  target,
  onClose,
}: {
  /** The administrator to reset, or null when closed. */
  readonly target: { readonly userId: string; readonly label: string } | null;
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useI18n();
  const mutation = useResetAdminPasswordMutation();
  const [method, setMethod] = useState<VerificationMethod | ''>('');
  const [issued, setIssued] = useState<Issued | null>(null);

  function close(): void {
    setMethod('');
    setIssued(null);
    onClose();
  }

  return (
    <>
      <PasswordActionDialog
        open={target !== null && issued === null}
        title={t('admin.access.reset.title')}
        warning={t('admin.access.reset.warning')}
        confirmLabel={t('admin.access.reset.submit')}
        {...(target === null ? {} : { subject: target.label })}
        onClose={close}
        extraValid={method !== ''}
        onConfirm={async ({ reason, password }) => {
          if (target === null || method === '') return;
          const result = await mutation.mutateAsync({
            userId: target.userId,
            reason,
            verificationMethod: method,
            confirmPassword: password,
          });
          setIssued({
            account: target.label,
            password: result.temporaryPassword,
            expiresAt: result.expiresAt,
          });
        }}
      >
        <Field
          label={t('admin.access.reset.methodLabel')}
          hint={t('admin.access.reset.methodHint')}
        >
          {(bind) => (
            <Select
              {...bind}
              placeholder={t('admin.access.reset.methodPlaceholder')}
              options={VERIFICATION_METHODS.map((value) => ({
                value,
                label: t(METHOD_LABEL_KEY[value]),
              }))}
              value={method}
              onChange={(event) => {
                setMethod(event.target.value as VerificationMethod | '');
              }}
            />
          )}
        </Field>
      </PasswordActionDialog>

      <Modal open={issued !== null} title={t('admin.access.reset.title')} onClose={close}>
        {issued !== null ? (
          <TemporaryPasswordPanel
            account={issued.account}
            password={issued.password}
            expiresAt={issued.expiresAt}
            onDone={close}
            onExpire={() => {
              setIssued({ ...issued, password: '' });
            }}
          />
        ) : null}
      </Modal>
    </>
  );
}
